import { useTheme } from "../../lib/theme";

/**
 * AutoGlow Brand · Sprint Visual V2 (Feb 2026)
 *
 * UNIFICACIÓN VISUAL — el wordmark inline ahora incorpora los DOS detalles
 * premium que diferencian al logo oficial descargable:
 *   1. ✨ Sparkle dorada · estrella de 4 puntas sobre la "A" de Auto
 *   2. ⌒ Arco dinámico  · curva dorada que une "Auto" con "Glow"
 *
 * Mantenemos:
 *   · Línea 1 "AutoGlow"  → Inter Tight 700 · "Auto" según tema · "Glow" siempre #FFB703
 *   · Línea 2 "ECOSYSTEM" → uppercase muted (no aqua) para que el oro lidere
 *
 * Esto unifica:
 *   – Logo navbar (GlobalHeader, AppShell)
 *   – Logo descargable (BrandAssetsCard)
 *   – Logo SuperAdmin
 *
 * Una sola identidad. Una sola fuente de verdad.
 */
export function AutoGlowBrand({ size = 28, className = "", showTagline = true, autoColor, showOrnaments = true }) {
  const { theme } = useTheme();
  const baseAuto = autoColor || (theme === "light" ? "#1A1A1A" : "#FFFFFF");
  const taglineColor = theme === "light" ? "#94A3B8" : "#8B93A7";
  const tagSize = Math.max(8, Math.round(size * 0.32));
  const gold = "#FFB703";

  // Adornos proporcionales al tamaño del wordmark
  const sparkleSize = Math.max(6, Math.round(size * 0.30));
  const arcWidth = Math.max(60, Math.round(size * 4));
  const arcHeight = Math.max(8, Math.round(size * 0.28));
  const arcThickness = Math.max(1.2, size * 0.045);

  return (
    <div
      className={`inline-flex flex-col items-center leading-none select-none shrink-0 ${className}`}
      style={{
        fontFamily: '"Inter Tight", Inter, system-ui, sans-serif',
        paddingLeft: 2,
        paddingRight: 2,
      }}
      aria-label="AutoGlow Ecosystem"
      data-testid="autoglow-brand"
    >
      {/* Sparkle dorada · ornamento premium */}
      {showOrnaments && (
        <span
          aria-hidden="true"
          style={{
            display: "block",
            height: sparkleSize,
            marginBottom: Math.max(2, Math.round(size * 0.06)),
            transform: `translateX(-${Math.round(size * 1.5)}px)`,
            pointerEvents: "none",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 14 14"
            width={sparkleSize}
            height={sparkleSize}
            style={{ display: "block" }}
          >
            <path
              d="M7 0 L8.2 5.8 L14 7 L8.2 8.2 L7 14 L5.8 8.2 L0 7 L5.8 5.8 Z"
              fill={gold}
            />
          </svg>
        </span>
      )}

      {/* Wordmark "AutoGlow" + arco dorado debajo */}
      <span
        className="inline-flex items-baseline relative whitespace-nowrap"
        style={{
          fontWeight: 700,
          fontSize: size,
          letterSpacing: "-0.028em",
          color: baseAuto,
          lineHeight: 1,
        }}
      >
        <span style={{ position: "relative" }}>Auto</span>
        <span style={{ color: gold, position: "relative", marginLeft: size * 0.02 }}>
          Glow
        </span>

        {/* Arco dorado dinámico bajo el wordmark */}
        {showOrnaments && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${arcWidth} ${arcHeight}`}
            width={arcWidth}
            height={arcHeight}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: -Math.max(4, Math.round(size * 0.18)),
              pointerEvents: "none",
            }}
          >
            <path
              d={`M2 ${arcHeight - 1.5} Q ${arcWidth / 2} 1 ${arcWidth - 2} ${arcHeight - 1.5}`}
              fill="none"
              stroke={gold}
              strokeWidth={arcThickness}
              strokeLinecap="round"
              opacity="0.9"
            />
          </svg>
        )}
      </span>

      {/* ECOSYSTEM muted */}
      {showTagline && (
        <span
          style={{
            fontSize: tagSize,
            color: taglineColor,
            fontWeight: 600,
            letterSpacing: `${Math.max(2, Math.round(size * 0.16))}px`,
            marginTop: Math.max(6, Math.round(size * 0.26)),
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          Ecosystem
        </span>
      )}
    </div>
  );
}

/** Versión sin "ECOSYSTEM" — útil para footers compactos. */
export function AutoGlowWordmark(props) {
  return <AutoGlowBrand {...props} showTagline={false} />;
}

/** Versión sin adornos sparkle+arc — útil para zonas muy compactas (sidebar collapsed). */
export function AutoGlowWordmarkBare(props) {
  return <AutoGlowBrand {...props} showOrnaments={false} />;
}

/**
 * COMPATIBILIDAD ATRÁS — `AutoGlowMark` antes era la silueta del coche.
 * Devuelve null para que los `<AutoGlowMark />` antiguos desaparezcan limpiamente.
 */
export function AutoGlowMark() {
  return null;
}

export default AutoGlowBrand;
